import type { CSSProperties } from 'react';

import { isPaletteColorKey, type ColorChoice, type PaletteColorKey } from '@/features/storefront/colorChoice';
import { withContrast } from '@/features/storefront/colorMode/palette';
import type { StorefrontTheme, ThemeColors, ThemeFooter, ThemeHeader } from '@/features/storefront/themeSchema';
import { effectiveTokens } from '@/features/storefront/tokens';
import { contrastRatio, isHexColor, MIN_TEXT_CONTRAST, mixHex } from '@/lib/color';

/**
 * An area's own colours - the header, the footer, the announcement rail - as
 * styles.
 *
 * Every theme's chrome is drawn from `--storefront-background`, `-text`,
 * `-muted` and `-surface`, so an area is recoloured by redeclaring those four
 * on a wrapper around it rather than by teaching nine themes a new setting:
 * a dark masthead is the theme's own masthead, drawn inside a dark page.
 * Nothing here returns a style for an area the merchant has not touched, so
 * a shop that never opens these settings renders exactly as it did.
 */

/**
 * What a choice paints, as a CSS value. A palette slot reads the page-level
 * copy of the palette rather than `--storefront-<slot>`, because inside a
 * recoloured area those are the area's own - "Text" in a footer's divider means
 * the theme's text colour, not the footer's.
 */
export function resolveColor(choice: ColorChoice): string | undefined {
  if (isPaletteColorKey(choice)) return `var(--storefront-palette-${choice})`;
  if (isHexColor(choice)) return choice.trim();
  return undefined;
}

/**
 * The colours a choice is resolved against outside the shop: the palette, and
 * the theme's accent token where the caller knows it.
 */
export type ChoicePalette = ThemeColors & { accent?: string | null };

/** A theme's palette with its accent token, as the page paints them. */
export function choicePalette(theme: StorefrontTheme): ChoicePalette {
  return { ...theme.colors, accent: effectiveTokens(theme).accent ?? null };
}

/** The hex one palette slot paints - `accent` falling back to primary, as on the page. */
export function paletteHex(colors: ChoicePalette, key: PaletteColorKey): string | undefined {
  const value = key === 'accent' ? (colors.accent ?? colors.primary) : colors[key];
  return isHexColor(value) ? value.trim() : undefined;
}

/** The hex a choice paints against a palette, for the editor's contrast check. */
export function choiceHex(choice: ColorChoice, colors: ChoicePalette): string | undefined {
  if (isPaletteColorKey(choice)) return paletteHex(colors, choice);
  if (isHexColor(choice)) return choice.trim();
  return undefined;
}

/** `from` moved `amount` of the way to `to`, in JS where both are hexes and in CSS where not. */
function mix(from: string, to: string, amount: number): string {
  if (isHexColor(from) && isHexColor(to)) return mixHex(from, to, amount);
  return `color-mix(in srgb, ${from} ${Math.round((1 - amount) * 100)}%, ${to})`;
}

/**
 * The four chrome variables, redeclared for one area, or `null` when the
 * merchant chose neither colour. Each colour changes only itself: a background
 * never picks a text colour, because a merchant who sets one and sees the
 * other move reads it as the picker being broken - the editor warns about an
 * unreadable pair instead. The quiet text and the border shade are drawn from
 * the two colours, so a theme's hairlines and secondary links stay in
 * proportion on whatever background the area now has.
 */
export function colorScope(background: ColorChoice, text: ColorChoice): CSSProperties | null {
  const bg = resolveColor(background);
  const fg = resolveColor(text);
  if (!bg && !fg) return null;

  const base = bg ?? 'var(--storefront-background)';
  const ink = fg ?? 'var(--storefront-text)';
  const style: Record<string, string> = {
    '--storefront-muted': mix(ink, base, 0.35),
    '--storefront-surface': mix(base, ink, 0.12),
  };
  if (bg) {
    style.backgroundColor = bg;
    style['--storefront-background'] = bg;
  }
  if (fg) {
    style.color = fg;
    style['--storefront-text'] = fg;
  }
  return style as CSSProperties;
}

function divider(px: number | null | undefined, color: ColorChoice): CSSProperties {
  if (!px || px <= 0) return {};
  return { borderBottom: `${px}px solid ${resolveColor(color) ?? 'var(--storefront-surface)'}` };
}

function px(value: number | null | undefined): string | undefined {
  return value == null ? undefined : `${value}px`;
}

/**
 * The brand colour, made to read on an area's own background. The chrome's
 * icons and links - search, wishlist, cart, the mode toggle - are painted in
 * `--storefront-primary`, and a header in the brand's own colour (a lime
 * masthead whose dark-mode primary is that same lime) would paint them
 * invisible. Only where the merchant gave the area a background and the two
 * clash; the hue is kept, so it is still recognisably their brand.
 */
function readablePrimary(background: ColorChoice, palette: ChoicePalette | undefined): CSSProperties {
  if (!palette) return {};
  const bg = choiceHex(background, palette);
  const primary = paletteHex(palette, 'primary');
  if (!bg || !primary || contrastRatio(primary, bg) >= MIN_TEXT_CONTRAST) return {};
  return { '--storefront-primary': withContrast(primary, bg, MIN_TEXT_CONTRAST) } as CSSProperties;
}

/**
 * The masthead's wrapper style, or `null` for the theme's own masthead. With
 * the palette it is drawn against, the brand colour is held readable on the
 * merchant's header background (see `readablePrimary`).
 */
export function headerStyle(header: ThemeHeader, palette?: ChoicePalette): CSSProperties | null {
  const scope = colorScope(header.background, header.text_color);
  const line = divider(header.divider_px, header.divider_color);
  if (!scope && Object.keys(line).length === 0) return null;
  return { ...scope, ...readablePrimary(header.background, palette), ...line };
}

/**
 * The announcement rail's two layers: the band, and the row of text inside
 * it. A page-width rail carries its colour on the row, so the band stops at
 * the page's edges instead of running to the screen's.
 */
export function announcementStyle(header: ThemeHeader): { band: CSSProperties; row: CSSProperties } {
  // The theme's text colour carrying its background colour, so the rail
  // inverts correctly whatever palette the merchant lands on - a dark theme
  // gets a light rail rather than a black bar that vanishes. The palette's own
  // names, because the rail sits inside the header: read through the header's
  // variables, recolouring the header's text would repaint the rail's band.
  const colors: CSSProperties = {
    backgroundColor: 'var(--storefront-palette-text, var(--storefront-text))',
    color: 'var(--storefront-palette-background, var(--storefront-background))',
    ...colorScope(header.announcement_background, header.announcement_text_color),
  };
  const spacing: CSSProperties = {
    paddingTop: px(header.announcement_padding_top_px),
    paddingBottom: px(header.announcement_padding_bottom_px),
  };
  const line = divider(header.announcement_divider_px, header.announcement_divider_color);

  if (header.announcement_width === 'page') {
    return { band: line, row: { ...colors, ...spacing } };
  }
  return { band: { ...colors, ...line }, row: spacing };
}

/** The footer's wrapper style, or `null` for the theme's own footer. Takes the palette as `headerStyle` does. */
export function footerStyle(footer: ThemeFooter | undefined, palette?: ChoicePalette): CSSProperties | null {
  if (!footer) return null;
  const scope = colorScope(footer.background, footer.text_color);
  const top = footer.padding_top_px ?? 0;
  const bottom = footer.padding_bottom_px ?? 0;
  if (!scope && top <= 0 && bottom <= 0) return null;
  return {
    ...scope,
    ...readablePrimary(footer.background, palette),
    paddingTop: top > 0 ? `${top}px` : undefined,
    paddingBottom: bottom > 0 ? `${bottom}px` : undefined,
  };
}

/**
 * The large footer's bottom bar: the merchant's colours, or - unset - a shade
 * deeper than the footer it closes, drawn from the footer's own variables so
 * it follows a recoloured footer.
 */
export function bottomBarStyle(footer: ThemeFooter | undefined): CSSProperties {
  return {
    backgroundColor: 'color-mix(in srgb, var(--storefront-text) 7%, var(--storefront-background))',
    borderTop: '1px solid var(--storefront-surface)',
    ...colorScope(footer?.bottom_bar_background, footer?.bottom_bar_text_color),
  };
}

/**
 * The benefits strip above the footer: the theme's text colour carrying its
 * background colour - the announcement rail's inversion, which reads on any
 * palette - unless the merchant chose their own.
 */
export function ribbonStyle(footer: ThemeFooter | undefined): CSSProperties {
  return {
    backgroundColor: 'var(--storefront-palette-text, var(--storefront-text))',
    color: 'var(--storefront-palette-background, var(--storefront-background))',
    ...colorScope(footer?.ribbon_background, footer?.ribbon_text_color),
  };
}

/** The copyright line's size and case, over whatever the theme sets it in. */
export function copyrightStyle(footer: ThemeFooter | undefined): CSSProperties | undefined {
  if (!footer) return undefined;
  const size = px(footer.copyright_size_px);
  const upper = footer.copyright_uppercase === true;
  if (!size && !upper) return undefined;
  return {
    fontSize: size,
    textTransform: upper ? 'uppercase' : undefined,
    letterSpacing: upper ? '0.06em' : undefined,
  };
}

/** What a section or a block carries of its own look - see `sections/schema.ts`. */
export interface AreaLook {
  background_color?: ColorChoice;
  text_color?: ColorChoice;
  divider_px?: number | null;
  divider_color?: ColorChoice;
  padding_top_px?: number | null;
  padding_bottom_px?: number | null;
  padding_left_px?: number | null;
  padding_right_px?: number | null;
}

/**
 * A section's or a block's own colours, line and exact padding, laid over the
 * style its presets give it. Empty for one the merchant has not touched.
 * `bleeds` keeps padding off a band whose artwork runs to its edges.
 */
export function areaStyle(look: AreaLook, { bleeds = false }: { bleeds?: boolean } = {}): CSSProperties {
  return {
    ...colorScope(look.background_color, look.text_color),
    ...divider(look.divider_px, look.divider_color),
    ...(bleeds
      ? {}
      : {
          paddingTop: px(look.padding_top_px),
          paddingBottom: px(look.padding_bottom_px),
          paddingLeft: px(look.padding_left_px),
          paddingRight: px(look.padding_right_px),
        }),
  };
}
